/**
 * Gestione piani commerciali (SuperAdmin MAE).
 *
 * I 5 piani live oggi: FREE / STARTER / BASIC / PRO / ENTERPRISE.
 * Per pricing/bundle items rimandiamo al dettaglio del singolo piano
 * (qui esponiamo solo il catalogo, niente edit pricing inline — quello
 * vive su license-pricings e license-plan-items).
 */
import { createFileRoute } from '@tanstack/react-router';
import { Badge, Group, Stack, Text, Title } from '@mantine/core';
import { IconLicense } from '@tabler/icons-react';
import { ResourceListPage } from '@/shared/components/ResourceListPage';
import { QuickCreateButton } from '@/shared/components/QuickCreateButton';
import type { LicensePlan } from '@/shared/types/api.gen';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/admin/license-plans')({ component: LicensePlansPage });

type Row = LicensePlan & Denormalized & { id: string };

function LicensePlansPage() {
  return (
    <Stack>
      <Group>
        <IconLicense size={28} />
        <Title order={1}>Piani commerciali</Title>
      </Group>
      <Text c="dimmed" size="sm">
        Catalogo piani vendibili. Per modificare bundle (componenti inclusi) o pricing multi-valuta, usa le risorse <code>license-plan-items</code> e <code>license-pricings</code>.
      </Text>

      <ResourceListPage<Row>
        title=""
        type="license-plans"
        enableSort
        searchField="name"
        columns={[
          { accessorKey: 'sortOrder', header: '#', size: 60 },
          { accessorKey: 'code', header: 'Codice', size: 140 },
          { accessorKey: 'name', header: 'Nome' },
          { accessorKey: 'tier', header: 'Tier', size: 100, Cell: ({ cell }) => {
            const v = cell.getValue<string | undefined>();
            return v ? <Badge variant="light" size="sm">{v}</Badge> : null;
          } },
          { accessorKey: 'targetSegment', header: 'Segmento', size: 140 },
          { accessorKey: 'maxSeats', header: 'Max seats', size: 100 },
          { accessorKey: 'isPubliclyVisible', header: 'Pubblico', size: 90, Cell: ({ cell }) => cell.getValue<boolean>() ? <Badge color="green" size="xs">Sì</Badge> : <Badge color="gray" size="xs">No</Badge> },
          { accessorKey: 'isTrialEligible', header: 'Trial', size: 80, Cell: ({ cell }) => cell.getValue<boolean>() ? <Badge color="cyan" size="xs">Sì</Badge> : null },
          { accessorKey: 'isActive', header: 'Attivo', size: 80, Cell: ({ cell }) => cell.getValue<boolean>() ? <Badge color="green" size="xs">Sì</Badge> : <Badge color="red" size="xs">No</Badge> },
        ]}
        emptyMessage="Nessun piano commerciale"
        emptyDescription="Crea il primo piano con il bottone «Nuovo piano» nella toolbar."
        emptyIcon={IconLicense}
        toolbarRight={
          <QuickCreateButton
            type="license-plans"
            title="Nuovo piano"
            fields={[
              { name: 'code', label: 'Codice (uppercase)', required: true, placeholder: 'STARTER' },
              { name: 'name', label: 'Nome commerciale', required: true },
              { name: 'description', label: 'Descrizione', kind: 'textarea' },
              { name: 'tier', label: 'Tier', kind: 'select', options: [
                { value: 'free', label: 'Free' },
                { value: 'starter', label: 'Starter' },
                { value: 'basic', label: 'Basic' },
                { value: 'pro', label: 'Pro' },
                { value: 'enterprise', label: 'Enterprise' },
              ] },
              { name: 'targetSegment', label: 'Segmento target' },
              { name: 'maxSeats', label: 'Max seats', kind: 'number' },
              { name: 'sortOrder', label: 'Ordine visualizzazione', kind: 'number', defaultValue: 100 },
              { name: 'isPubliclyVisible', label: 'Pubblicamente visibile', kind: 'boolean', defaultValue: true },
              { name: 'isTrialEligible', label: 'Eleggibile a trial', kind: 'boolean', defaultValue: false },
              { name: 'trialDurationDays', label: 'Giorni trial', kind: 'number' },
              { name: 'isActive', label: 'Attivo', kind: 'boolean', defaultValue: true },
            ]}
          />
        }
      />
    </Stack>
  );
}
